/**
 * Validators for values read back out of localStorage, for use as the `revive`
 * argument to `useStoredState`. Kept React-free and side-effect-free.
 *
 * A stored value outlives the code that wrote it: it may predate a rename, come
 * from a different app version, or have been hand-edited. Each validator returns
 * `undefined` for anything it does not recognise, which makes the caller fall
 * back to its default rather than propagating junk into the UI.
 */

/** One of a known set — enums, radio options, discrete slider steps. */
export function storedOneOf<T>(allowed: readonly T[]): (raw: unknown) => T | undefined {
  return (raw) => (allowed.includes(raw as T) ? (raw as T) : undefined);
}

/**
 * A finite number, clamped into a fixed range. Only use the bounds for limits
 * that don't depend on loaded data — a bound derived from a run must be applied
 * where the value is read, so the stored preference survives for other runs.
 */
export function storedNumber(min = -Infinity, max = Infinity): (raw: unknown) => number | undefined {
  return (raw) => {
    if (typeof raw !== 'number' || !Number.isFinite(raw)) return undefined;
    return Math.max(min, Math.min(max, raw));
  };
}
